import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EditableText } from "@mapmycustomers/ui";

import Chip from "@app/component/Chip";
import configService from "@app/config/ConfigService";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import ViewType from "@app/scene/dashboard/enum/ViewType";
import { addCard } from "@app/scene/dashboard/store/actions";
import { getBoards } from "@app/scene/dashboard/store/selectors";
import canEditBoard from "@app/scene/dashboard/utils/canEditBoard";
import authService from "@app/store/auth/AuthService";
import { createFile } from "@app/store/files/actions";
import {
  getCurrentUser,
  getOrganization,
  getOrganizationSetting,
  isCurrentUserOwner,
} from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import Dashboard from "@app/types/dashboard/Dashboard";
import Scope from "@app/types/dashboard/Scope";
import { getLegacyAppUrl } from "@app/util/appUrl";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { complementFileUrl } from "@app/util/file";

import AddCardWizard from "../../addEditCard/AddCardWizard";
import useAddCard from "../../DashboardContent/util/useAddCard";

import BaseHeader from "./BaseHeader";
import DashboardOverrideModal from "./DashboardOverrideModal";
import EllipsisMenu from "./EllipsisMenu";
import styles from "./HeaderCommon.module.scss";
import Image from "./Image";
import messages from "./messages";

interface OrganizationHeaderContentProps {
  auto?: boolean;
  colors?: AnyColor[];
  onGradientChange?: (
    settings: Partial<Pick<Dashboard["settings"], "gradient" | "gradientFromImage">>
  ) => void;
  view: ViewType;
}

interface Props extends OrganizationHeaderContentProps {
  boards: Dashboard[];
  currentUser: User;
  editable?: boolean;
  isOwner?: boolean;
  onAddCard: typeof addCard.request;
  onCreateFile?: typeof createFile.request;
  onUpdateOrganization?: typeof updateOrganization.request;
  organization: Organization;
  organizationLogoSetting?: Setting;
  scope: Scope;
}

const OrganizationHeaderContent: React.FC<Props> = ({
  auto,
  boards,
  colors,
  currentUser,
  editable,
  isOwner,
  onAddCard,
  onCreateFile,
  onGradientChange,
  onUpdateOrganization,
  organization,
  organizationLogoSetting,
  scope,
  view,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const logoUrl = complementFileUrl(organizationLogoSetting?.value);
  const [editing, setEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [addCardWizardVisible, showAddCardWizard, hideAddCardWizard] = useBoolean();
  const history = useHistory();

  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();
  const handleUsersChipClick = useCallback(() => {
    const url = getLegacyAppUrl(baseOldAppUrl, token, "/dashboard/teams");
    window.open(url, "_blank");
  }, [baseOldAppUrl, token]);

  const handleChangeOrganizationName = useCallback(
    (name: string) => {
      if (!name.trim().length) {
        return;
      }
      onUpdateOrganization?.({
        organization: { id: organization.id, name },
      });
    },
    [onUpdateOrganization, organization.id]
  );

  const handleOrgPreferencesClick = useCallback(() => {
    history.push(`${Path.SETTINGS}/${SettingPath.ORGANIZATION_DETAILS}`);
  }, [history]);

  const handleManageUsersClick = useCallback(() => {
    const url = getLegacyAppUrl(baseOldAppUrl, token, "/dashboard/teams");
    window.open(url, "_blank");
  }, [baseOldAppUrl, token]);

  const handleRemoveLogo = useCallback(() => {
    analytics.clicked(["Change org picture"]);
    onUpdateOrganization?.({
      organization: { id: organization.id },
      settings: [{ ...organizationLogoSetting!, value: "" }],
    });
  }, [organization.id, organizationLogoSetting, onUpdateOrganization, analytics]);

  const handleChangeLogo = useCallback(
    (file: File) => {
      onCreateFile?.({
        callback: (file: RawFile) => {
          onUpdateOrganization?.({
            organization: { id: organization.id },
            settings: [{ ...organizationLogoSetting!, value: file.publicURI }],
          });
          analytics.clicked(["Change org picture"]);
        },
        file,
      });
    },
    [organization.id, organizationLogoSetting, onCreateFile, onUpdateOrganization, analytics]
  );

  const canChangeLogo = isOwner && !!organizationLogoSetting;

  const canAddCharts = canEditBoard(currentUser, scope, "add");
  const handleAddChart = useAddCard(boards, currentUser, scope, onAddCard);

  if (!token) {
    return null;
  }

  return (
    <BaseHeader
      auto={auto}
      colors={colors}
      editable={editable}
      logoUrl={logoUrl}
      onGradientChange={onGradientChange}
    >
      {isOwner || logoUrl ? (
        <Image
          alt="organization logo"
          aspect={false}
          className={styles.image}
          onChange={canChangeLogo ? handleChangeLogo : undefined}
          onRemove={canChangeLogo ? handleRemoveLogo : undefined}
          src={logoUrl}
        >
          {intl.formatMessage(canChangeLogo ? messages.addCompanyLogo : messages.noCompanyLogo)}
        </Image>
      ) : null}
      <div className={styles.info}>
        <EditableText
          className={cn(styles.name, { [styles.nameEditing]: editing })}
          editable={isOwner}
          editing={editing}
          onChange={handleChangeOrganizationName}
          onEditingChange={setEditing}
          required
          value={organization.name}
        />
        <Tooltip placement="bottom" title={intl.formatMessage(messages.manageUsersTooltip)}>
          <Chip className={styles.chip} onClick={handleUsersChipClick}>
            {intl.formatMessage(messages.usersCount, { count: organization.teamSize })}
          </Chip>
        </Tooltip>
      </div>
      {addCardWizardVisible && (
        <AddCardWizard onAddCard={handleAddChart} onHide={hideAddCardWizard} scope={scope} />
      )}
      <EllipsisMenu
        items={[
          {
            key: "preferences",
            label: intl.formatMessage(messages.orgPreferences),
            onClick: handleOrgPreferencesClick,
          },
          {
            key: "manageUsers",
            label: intl.formatMessage(messages.manageUsers),
            onClick: handleManageUsersClick,
          },
          ...(view === ViewType.DASHBOARD
            ? [
                {
                  className: styles.applyDashboard,
                  key: "applyOrgDashboard",
                  label: intl.formatMessage(messages.applyOrgDashboard),
                  onClick: () => setModalOpen(true),
                },
              ]
            : []),
          ...(canAddCharts && view === ViewType.DASHBOARD
            ? [
                {
                  key: "addChart",
                  label: intl.formatMessage(messages.addChart),
                  onClick: showAddCardWizard,
                },
              ]
            : []),
        ]}
      />
      <DashboardOverrideModal onModalOpen={setModalOpen} open={modalOpen} />
    </BaseHeader>
  );
};

const mapStateToProps = (state: RootState) => ({
  boards: getBoards(state),
  currentUser: getCurrentUser(state)!,
  isOwner: isCurrentUserOwner(state),
  organization: getOrganization(state)!,
  organizationLogoSetting: getOrganizationSetting(state)(OrganizationSetting.LOGO),
});

const mapDispatchToProps = {
  onAddCard: addCard.request,
  onCreateFile: createFile.request,
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationHeaderContent);
